

export const frontMatter = {
	sidebar_position: 0
};
export const contentTitle = 'Getting started';
export {default as metadata} from '@site/.docusaurus/docusaurus-plugin-content-docs/default/site-docs-getting-started-md-d1f.json'
export const assets = {

};

import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import Tabs from '@theme/Tabs';
import TabItem from '@theme/TabItem';
export const toc = [{
  "value": "Installation",
  "id": "installation",
  "level": 3
}, {
  "value": "Loading your first image in Node.js",
  "id": "loading-your-first-image-in-nodejs",
  "level": 3
}, {
  "value": "Loading your first image in browser",
  "id": "loading-your-first-image-in-browser",
  "level": 3
}, {
  "value": "Bundling your image with Vite",
  "id": "bundling-your-image-with-vite",
  "level": 3
}, {
  "value": "What&#39;s next?",
  "id": "whats-next",
  "level": 3
}, {
  "value": "System requirements",
  "id": "system-requirements",
  "level": 3
}];
function _createMdxContent(props) {
  const _components = {
    a: "a",
    admonition: "admonition",
    code: "code",
    h1: "h1",
    h3: "h3",
    header: "header",
    li: "li",
    p: "p",
    pre: "pre",
    ul: "ul",
    ..._provideComponents(),
    ...props.components
  };
  return _jsxs(_Fragment, {
    children: [_jsx(_components.header, {
      children: _jsx(_components.h1, {
        id: "getting-started",
        children: "Getting started"
      })
    }), "\n", _jsx(_components.p, {
      children: "Image-JS is a versatile and powerful library written in TypeScript for image processing and analysis. It gives a user a comprehensive set of tools and algorithms for manipulating, enhancing, and understanding images not only within Node.js but also across all popular browsers."
    }), "\n", _jsx(_components.h3, {
      id: "installation",
      children: "Installation"
    }), "\n", _jsx(_components.p, {
      children: "Installation of ImageJS is straight-forward. Use terminal to install the package:"
    }), "\n", _jsxs(Tabs, {
      children: [_jsx(TabItem, {
        value: "npm",
        label: "npm",
        default: true,
        children: _jsx(_components.pre, {
          children: _jsx(_components.code, {
            children: "npm install image-js\n"
          })
        })
      }), _jsx(TabItem, {
        value: "yarn",
        label: "yarn",
        children: _jsx(_components.pre, {
          children: _jsx(_components.code, {
            children: "yarn add image-js\n"
          })
        })
      })]
    }), "\n", _jsx(_components.p, {
      children: "There are two ways of loading an image to process it, depending on the way the user is operating: to load locally and load through the browser."
    }), "\n", _jsx(_components.h3, {
      id: "loading-your-first-image-in-nodejs",
      children: "Loading your first image in Node.js"
    }), "\n", _jsxs(_components.p, {
      children: ["Local loading uses ", _jsx(_components.code, {
        children: "readSync"
      }), " function with indicated filepath:"]
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-ts",
        children: "import { readSync } from 'image-js';\n\nconst parsedImage = readSync('../example.jpg');\n"
      })
    }), "\n", _jsx(_components.admonition, {
      type: "tip",
      children: _jsxs(_components.p, {
        children: ["Node.js can also load an image via ", _jsx(_components.code, {
          children: "fetchURL"
        }), ". To get more information take a look at ", _jsx(_components.a, {
          href: "#loading-your-first-image-in-browser",
          children: "\"Browser\""
        }), " part of this section."]
      })
    }), "\n", _jsxs(_components.p, {
      children: ["Once the image is loaded, it returns an instance of the ", _jsx(_components.code, {
        children: "Image"
      }), " class, so its methods can be applied."]
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-ts",
        children: "const image = parsedImage.invert();\n"
      })
    }), "\n", _jsxs(_components.p, {
      children: ["To save an image use ", _jsx(_components.code, {
        children: "writeSync"
      }), " function:"]
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-ts",
        children: "writeSync('../example.jpg', image);\n"
      })
    }), "\n", _jsxs(_components.p, {
      children: ["Image format is automatically identified based on the extension typed by the user. In this case it's ", _jsx(_components.code, {
        children: "'.jpg'"
      }), "."]
    }), "\n", _jsx(_components.p, {
      children: "So, in the end you should get a code like this."
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-ts",
        children: "import { readSync, writeSync, Image } from 'image-js';\n\nconst parsedImage = readSync('../example.jpg');\nconst image = parsedImage.invert();\nwriteSync('../example.jpg', image);\n"
      })
    }), "\n", _jsx(_components.h3, {
      id: "loading-your-first-image-in-browser",
      children: "Loading your first image in browser"
    }), "\n", _jsxs(_components.p, {
      children: ["To load an image via browser, in order to instantiate it, you may use ", _jsx(_components.code, {
        children: "fetchURL"
      }), " function:"]
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-ts",
        children: "import { fetchURL } from 'image-js';\n\nlet image = await fetchURL('https:://example.com/image.jpg'); // image is ready for usage\nimage = image.grey();\n"
      })
    }), "\n", _jsx(_components.admonition, {
      type: "info",
      children: _jsxs(_components.p, {
        children: ["To see more methods visit ", _jsx(_components.a, {
          href: "/docs/Features/",
          title: "internal link on features",
          children: "\"Features\""
        }), " category."]
      })
    }), "\n", _jsx(_components.h3, {
      id: "bundling-your-image-with-vite",
      children: "Bundling your image with Vite"
    }), "\n", _jsxs(_components.p, {
      children: ["To display an image via ", _jsx(_components.a, {
        href: "https://en.wikipedia.org/wiki/Document_Object_Model",
        title: "wikipedia link on dom",
        children: "DOM"
      }), " you can use ", _jsx(_components.code, {
        children: "writeCanvas"
      }), " method."]
    }), "\n", _jsx(_components.p, {
      children: "To do so, you need to create a Node.js project:"
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        children: "npm init\n"
      })
    }), "\n", _jsxs(_components.p, {
      children: ["After creating one, install ", _jsx(_components.code, {
        children: "vite"
      }), " as a dev-dependency and then install ", _jsx(_components.code, {
        children: "image-js"
      }), ":"]
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        children: "npm install -D vite\n"
      })
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        children: "npm install image-js\n"
      })
    }), "\n", _jsxs(_components.p, {
      children: ["After this you can create a basic html page ", _jsx(_components.code, {
        children: "index.html"
      }), " with a ", _jsx(_components.code, {
        children: "<canvas>"
      }), " element:"]
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-html",
        children: "<!DOCTYPE html>\n<html lang=\"en\">\n  <head>\n    <meta charset=\"UTF-8\" />\n    <meta name=\"viewport\" content=\"width=device-width, initial-scale=1.0\" />\n    <title>image-js demo app</title>\n  </head>\n  <body>\n    <div id=\"root\"></div>\n    <!-- canvas element -->\n    <canvas id=\"my_canvas\" />\n  </body>\n</html>\n"
      })
    }), "\n", _jsxs(_components.p, {
      children: ["In the ", _jsx(_components.code, {
        children: "<body>"
      }), " part of your code insert your ", _jsx(_components.code, {
        children: "image-js"
      }), " script. For instance, here the script turns image into grayscale:"]
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-html",
        children: "<script type=\"module\">\n  import {(Image, writeCanvas)} from 'image-js';\n  let image = await fetchURL('https:://example.com/image.jpg'); // image is ready for usage\n  image = image.grey();\n  const canvas = document.getElementById('my_canvas');\n  writeCanvas(image, canvas);\n</script>\n"
      })
    }), "\n", _jsx(_components.admonition, {
      type: "caution",
      children: _jsx(_components.p, {
        children: "Don't forget to specify the script type. If it is not set as module, the script will not work."
      })
    }), "\n", _jsx(_components.p, {
      children: "Thus, in the end your html page should look like this:"
    }), "\n", _jsx(_components.pre, {
      children: _jsx(_components.code, {
        className: "language-ts",
        children: "<!DOCTYPE html>\n<html lang=\"en\">\n  <head>\n    <meta charset=\"UTF-8\" />\n    <meta name=\"viewport\" content=\"width=device-width, initial-scale=1.0\" />\n    <title>image-js demo app</title>\n  </head>\n  <body>\n    <div id=\"root\"></div>\n    <!-- canvas element -->\n    <canvas id=\"my_canvas\" />\n    <script type=\"module\">\n  import {Image, writeCanvas,decode} from 'image-js';\n  let image = await fetchURL('https:://example.com/image.jpg'); // image is ready for usage\n\n  image = image.grey();\n  const canvas = document.getElementById('my_canvas');\n  writeCanvas(image, canvas);\n</script>\n  </body>\n</html>\n"
      })
    }), "\n", _jsx(_components.h3, {
      id: "whats-next",
      children: "What's next?"
    }), "\n", _jsxs(_components.p, {
      children: ["Now that you know how images are loaded and saved you can deepen your understanding by going through ", _jsx(_components.a, {
        href: "./Basics",
        title: "internal link on basics",
        children: "Basics"
      }), " folder and see how different basic elements of ImageJS work. You can also broaden your horizons by looking at available ", _jsx(_components.a, {
        href: "./Features",
        title: "internal link on features",
        children: "Features"
      }), "."]
    }), "\n", _jsxs(_components.p, {
      children: ["If you want to see how ImageJS works in practice I suggest you visit the ", _jsx(_components.a, {
        href: "./Tutorials",
        title: "internal link on tutorial",
        children: "Tutorials"
      }), " segment and see for yourself its practical applications."]
    }), "\n", _jsx(_components.h3, {
      id: "system-requirements",
      children: "System requirements"
    }), "\n", _jsxs(_components.ul, {
      children: ["\n", _jsxs(_components.li, {
        children: ["\n", _jsx(_components.p, {
          children: "Node.js 16+"
        }), "\n"]
      }), "\n", _jsxs(_components.li, {
        children: ["\n", _jsx(_components.p, {
          children: "Google Chrome 91+, Firefox 90+, Safari 15+, Opera 77+, Edge 91+"
        }), "\n"]
      }), "\n"]
    })]
  });
}
export default function MDXContent(props = {}) {
  const {wrapper: MDXLayout} = {
    ..._provideComponents(),
    ...props.components
  };
  return MDXLayout ? _jsx(MDXLayout, {
    ...props,
    children: _jsx(_createMdxContent, {
      ...props
    })
  }) : _createMdxContent(props);
}

